import i18n from 'i18next';
import resourcesToBackend from 'i18next-resources-to-backend';
import { initReactI18next } from 'react-i18next';

import commonEn from './locales/en/common.json';
import commonEs from './locales/es/common.json';
import labelsEn from './locales/en/labels.json';
import labelsEs from './locales/es/labels.json';

export const SUPPORTED_LANGS = ['en', 'es'];

// `common` and `labels` are bundled into the entry chunk: `common` is what anything
// rendered outside <Suspense> (Footer, ErrorBoundary, OAuthHandler, UserLoading) may use,
// and `labels` (the closed vocabularies, #684) is read by shared components that mount
// after data arrives (EventRow, CitySwitcher's history) and by helpers handed any page's
// `t`. Every other namespace is its own chunk, fetched the first time a component asks for
// it — which suspends inside the route's <Suspense>, so a page still paints already
// translated. See "i18n" in apps/web/CLAUDE.md.
export const EAGER_NAMESPACES = ['common', 'labels'];
const lazyNamespaces = import.meta.glob([
  './locales/*/*.json',
  '!./locales/*/common.json',
  '!./locales/*/labels.json',
]);

// Every lazy namespace on disk — derived from the files so a new namespace
// needs no registration here.
export const LAZY_NAMESPACES = [
  ...new Set(Object.keys(lazyNamespaces).map((path) => path.match(/([^/]+)\.json$/)[1])),
];

// Where an explicit choice from `LanguageToggle` is kept. Only the toggle writes it — a
// detected language is never saved, so a reader who never chose keeps following the browser.
export const LANGUAGE_STORAGE_KEY = 'applauss-lang';

// A language with no catalog of its own → the one its readers read most easily (Spain's
// co-official languages read Spanish far more easily than English). Mirrors
// `LOCALE_FALLBACKS` in apps/api/config.py — change both together, so the UI and the
// API's `get_locale` pick the same language. A served language always resolves to itself
// first, so `ca` becomes Catalan the day it joins SUPPORTED_LANGS.
export const LOCALE_FALLBACKS = { ca: 'es', gl: 'es', eu: 'es' };

// A tag's primary subtag if served, else its LOCALE_FALLBACKS entry if that is served, else null.
function matchLang(tag) {
  const lang = String(tag ?? '').toLowerCase().split('-')[0];
  if (SUPPORTED_LANGS.includes(lang)) return lang;
  return SUPPORTED_LANGS.includes(LOCALE_FALLBACKS[lang]) ? LOCALE_FALLBACKS[lang] : null;
}

// The page's own `?lang=` (the admin's "View as reader" link, #839), matched like a browser
// tag → saved choice → the browser's languages, in the reader's own order, matched on their
// primary subtag → 'en'. Deliberately not an 'es' default: a French-only browser keeps
// English, the language the product was written in. `?lang=` is never saved: it holds for
// the page view it arrived with, and the next page opened without it follows the reader's
// usual language.
export function detectLang() {
  const requested = matchLang(new URLSearchParams(window.location.search).get('lang'));
  if (requested) return requested;
  try {
    const saved = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (SUPPORTED_LANGS.includes(saved)) return saved;
  } catch {
    // localStorage unavailable — fall through to the browser's languages
  }
  const languages = navigator.languages?.length ? navigator.languages : [navigator.language];
  for (const tag of languages) {
    const lang = matchLang(tag);
    if (lang) return lang;
  }
  return 'en';
}

// Must be registered before init(): with initAsync off, i18next resolves the
// language and fires 'languageChanged' synchronously inside init() itself.
i18n.on('languageChanged', (lng) => {
  document.documentElement.lang = lng;
});

i18n
  .use(resourcesToBackend((lng, ns) => {
    const load = lazyNamespaces[`./locales/${lng}/${ns}.json`];
    return load ? load() : Promise.reject(new Error(`No locale file for ${lng}/${ns}`));
  }))
  .use(initReactI18next)
  .init({
    resources: {
      en: { common: commonEn, labels: labelsEn },
      es: { common: commonEs, labels: labelsEs },
    },
    // The bundled `resources` hold only the eager namespaces; the backend fills the rest.
    partialBundledLanguages: true,
    // Synchronous init, so `common` is ready before the first render — the
    // components outside <Suspense> have no fallback to suspend into.
    initAsync: false,
    lng: detectLang(),
    fallbackLng: 'en',
    supportedLngs: SUPPORTED_LANGS,
    // A copy: i18next appends every namespace it loads to `ns`.
    ns: [...EAGER_NAMESPACES],
    defaultNS: 'common',
    interpolation: {
      escapeValue: false,
    },
  });

export default i18n;
